// src/services/messagesService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';

export class MessagesService {
  constructor() {
    this.messagesCollection = 'pb-event-messages';
    this.updatesCollection = 'pb-event-updates';
  }

  // ============ EVENT MESSAGES ============
  
  async getEventMessages(eventId) {
    try {
      const q = query(
        collection(db, this.messagesCollection),
        where('eventId', '==', eventId),
        where('status', 'in', ['approved', 'pending']),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event messages:', error);
      throw error;
    }
  }

  async sendEventMessage(eventId, messageData) {
    try {
      const newMessage = {
        eventId,
        content: messageData.content,
        type: 'message',
        authorId: messageData.authorId || 'anonymous',
        authorName: messageData.authorName || 'Anonymous User',
        authorEmail: messageData.authorEmail || null,
        authorAvatar: messageData.authorAvatar || null,
        isOrganizer: messageData.isOrganizer || false,
        status: messageData.needsModeration ? 'pending' : 'approved',
        likes: 0,
        reports: 0,
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
          ipAddress: null // Will be set by Cloud Function
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, this.messagesCollection), newMessage);
      
      // Send notification to organizers if needed
      if (!messageData.needsModeration) {
        await this.notifyOrganizers(eventId, 'new_message', {
          messageId: docRef.id,
          content: messageData.content,
          authorName: messageData.authorName
        });
      }

      return { id: docRef.id, ...newMessage };
    } catch (error) {
      console.error('Error sending event message:', error);
      throw error;
    }
  }

  async moderateMessage(messageId, action, moderatorId) {
    try {
      const messageRef = doc(db, this.messagesCollection, messageId);
      
      const updates = {
        status: action, // 'approved' or 'rejected'
        moderatedBy: moderatorId,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await updateDoc(messageRef, updates);
      
      // If approved, notify organizers
      if (action === 'approved') {
        const messageDoc = await getDoc(messageRef);
        const messageData = messageDoc.data();
        
        await this.notifyOrganizers(messageData.eventId, 'message_approved', {
          messageId,
          content: messageData.content,
          authorName: messageData.authorName
        });
      }

      return updates;
    } catch (error) {
      console.error('Error moderating message:', error);
      throw error;
    }
  }

  async toggleMessageLike(messageId, isLiked) {
    try {
      const messageRef = doc(db, this.messagesCollection, messageId);
      
      await updateDoc(messageRef, {
        likes: increment(isLiked ? 1 : -1),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error toggling message like:', error);
      throw error;
    }
  }

  async reportMessage(messageId, reportData) {
    try {
      const messageRef = doc(db, this.messagesCollection, messageId);
      
      // Add to reports subcollection
      await addDoc(collection(messageRef, 'reports'), {
        reason: reportData.reason,
        description: reportData.description,
        reporterId: reportData.reporterId,
        createdAt: serverTimestamp()
      });

      // Increment report count
      await updateDoc(messageRef, {
        reports: increment(1),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error reporting message:', error);
      throw error;
    }
  }

  // ============ EVENT UPDATES ============
  
  async getEventUpdates(eventId) {
    try {
      const q = query(
        collection(db, this.updatesCollection),
        where('eventId', '==', eventId),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event updates:', error);
      throw error;
    }
  }

  async sendEventUpdate(eventId, updateData) {
    try {
      const newUpdate = {
        eventId,
        title: updateData.title || 'Event Update',
        content: updateData.content,
        type: 'update',
        authorId: updateData.authorId,
        authorName: updateData.authorName,
        authorAvatar: updateData.authorAvatar || null,
        priority: updateData.priority || 'normal', // low, normal, high, urgent
        status: updateData.needsModeration ? 'pending' : 'published',
        category: updateData.category || 'general', // general, schedule, location, important
        notifyAttendees: updateData.notifyAttendees || false,
        metadata: {
          timestamp: Date.now(),
          version: '1.0'
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, this.updatesCollection), newUpdate);
      
      // Send notifications to attendees if enabled
      if (!updateData.needsModeration && updateData.notifyAttendees) {
        await this.notifyAttendees(eventId, 'new_update', {
          updateId: docRef.id,
          title: newUpdate.title,
          content: newUpdate.content,
          priority: newUpdate.priority
        });
      }

      return { id: docRef.id, ...newUpdate };
    } catch (error) {
      console.error('Error sending event update:', error);
      throw error;
    }
  }

  async moderateUpdate(updateId, action, moderatorId) {
    try {
      const updateRef = doc(db, this.updatesCollection, updateId);
      
      const updates = {
        status: action === 'approve' ? 'published' : 'rejected',
        moderatedBy: moderatorId,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await updateDoc(updateRef, updates);
      
      // If approved and notification enabled, notify attendees
      if (action === 'approve') {
        const updateDoc = await getDoc(updateRef);
        const updateData = updateDoc.data();
        
        if (updateData.notifyAttendees) {
          await this.notifyAttendees(updateData.eventId, 'new_update', {
            updateId,
            title: updateData.title,
            content: updateData.content,
            priority: updateData.priority
          });
        }
      }

      return updates;
    } catch (error) {
      console.error('Error moderating update:', error);
      throw error;
    }
  }

  // ============ MODERATION DASHBOARD ============
  
  async getModerationQueue(eventId, type = 'all') {
    try {
      const collections = [];
      
      if (type === 'all' || type === 'messages') {
        collections.push({
          collection: this.messagesCollection,
          type: 'message'
        });
      }
      
      if (type === 'all' || type === 'updates') {
        collections.push({
          collection: this.updatesCollection,
          type: 'update'
        });
      }

      const allItems = [];
      
      for (const col of collections) {
        const q = query(
          collection(db, col.collection),
          where('eventId', '==', eventId),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const items = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          itemType: col.type,
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        }));
        
        allItems.push(...items);
      }

      // Sort all items by creation date
      allItems.sort((a, b) => b.createdAt - a.createdAt);
      
      return allItems;
    } catch (error) {
      console.error('Error getting moderation queue:', error);
      throw error;
    }
  }

  async getModerationStats(eventId) {
    try {
      // Get pending messages
      const pendingMessagesQuery = query(
        collection(db, this.messagesCollection),
        where('eventId', '==', eventId),
        where('status', '==', 'pending')
      );
      const pendingMessages = await getDocs(pendingMessagesQuery);

      // Get pending updates
      const pendingUpdatesQuery = query(
        collection(db, this.updatesCollection),
        where('eventId', '==', eventId),
        where('status', '==', 'pending')
      );
      const pendingUpdates = await getDocs(pendingUpdatesQuery);

      // Get reported messages
      const reportedMessagesQuery = query(
        collection(db, this.messagesCollection),
        where('eventId', '==', eventId),
        where('reports', '>', 0)
      );
      const reportedMessages = await getDocs(reportedMessagesQuery);

      // Continuing from MessagesService.js...

      return {
        pendingMessages: pendingMessages.size,
        pendingUpdates: pendingUpdates.size,
        reportedMessages: reportedMessages.size,
        totalPending: pendingMessages.size + pendingUpdates.size
      };
    } catch (error) {
      console.error('Error getting moderation stats:', error);
      throw error;
    }
  }

  // ============ NOTIFICATION HELPERS ============
  
  async notifyOrganizers(eventId, type, data) {
    try {
      // Get event organizers
      const eventDoc = await getDoc(doc(db, 'pb-books', eventId));
      if (!eventDoc.exists()) return;
      
      const eventData = eventDoc.data();
      const organizers = eventData.organizers || [];
      
      // Create notification for each organizer
      const batch = writeBatch(db);
      
      organizers.forEach(organizer => {
        if (organizer.email) {
          const notificationRef = doc(collection(db, 'pb-notifications'));
          batch.set(notificationRef, {
            type,
            eventId,
            recipientEmail: organizer.email,
            recipientId: organizer.id,
            data,
            status: 'pending',
            createdAt: serverTimestamp()
          });
        }
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error notifying organizers:', error);
    }
  }

  async notifyAttendees(eventId, type, data) {
    try {
      // Get event attendees (booked users)
      const bookingsQuery = query(
        collection(db, 'pb-event-bookings'),
        where('eventId', '==', eventId),
        where('status', '==', 'confirmed')
      );
      
      const bookings = await getDocs(bookingsQuery);
      
      if (bookings.empty) return;

      // Create notification for each attendee
      const batch = writeBatch(db);
      
      bookings.docs.forEach(booking => {
        const bookingData = booking.data();
        if (bookingData.attendeeEmail) {
          const notificationRef = doc(collection(db, 'pb-notifications'));
          batch.set(notificationRef, {
            type,
            eventId,
            recipientEmail: bookingData.attendeeEmail,
            recipientId: bookingData.attendeeId,
            data,
            status: 'pending',
            createdAt: serverTimestamp()
          });
        }
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error notifying attendees:', error);
    }
  }

  // ============ BULK OPERATIONS ============
  
  async bulkModerateMessages(messageIds, action, moderatorId) {
    try {
      const batch = writeBatch(db);
      
      messageIds.forEach(messageId => {
        const messageRef = doc(db, this.messagesCollection, messageId);
        batch.update(messageRef, {
          status: action,
          moderatedBy: moderatorId,
          moderatedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      return { success: true, moderated: messageIds.length };
    } catch (error) {
      console.error('Error bulk moderating messages:', error);
      throw error;
    }
  }

  async deleteMessage(messageId) {
    try {
      await deleteDoc(doc(db, this.messagesCollection, messageId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  async deleteUpdate(updateId) {
    try {
      await deleteDoc(doc(db, this.updatesCollection, updateId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting update:', error);
      throw error;
    }
  }

  // ============ ANALYTICS ============
  
  async getMessageAnalytics(eventId, dateRange = 7) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const messagesQuery = query(
        collection(db, this.messagesCollection),
        where('eventId', '==', eventId),
        where('createdAt', '>=', startDate)
      );

      const updatesQuery = query(
        collection(db, this.updatesCollection),
        where('eventId', '==', eventId),
        where('createdAt', '>=', startDate)
      );

      const [messages, updates] = await Promise.all([
        getDocs(messagesQuery),
        getDocs(updatesQuery)
      ]);

      // Calculate metrics
      const messagesByDay = {};
      const updatesByDay = {};
      
      messages.docs.forEach(doc => {
        const date = doc.data().createdAt?.toDate().toISOString().split('T')[0];
        messagesByDay[date] = (messagesByDay[date] || 0) + 1;
      });

      updates.docs.forEach(doc => {
        const date = doc.data().createdAt?.toDate().toISOString().split('T')[0];
        updatesByDay[date] = (updatesByDay[date] || 0) + 1;
      });

      const totalLikes = messages.docs.reduce((sum, doc) => sum + (doc.data().likes || 0), 0);
      const totalReports = messages.docs.reduce((sum, doc) => sum + (doc.data().reports || 0), 0);

      return {
        totalMessages: messages.size,
        totalUpdates: updates.size,
        totalLikes,
        totalReports,
        messagesByDay,
        updatesByDay,
        averageMessagesPerDay: messages.size / dateRange,
        averageUpdatesPerDay: updates.size / dateRange
      };
    } catch (error) {
      console.error('Error getting message analytics:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const messagesService = new MessagesService();